import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Box,
  Avatar,
  Popover,
  TextField,
  Stack,
  Tooltip,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Checkbox,
  TableSortLabel,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ImageIcon from '@mui/icons-material/Image';
import ArchiveIcon from '@mui/icons-material/Archive';
import PrintIcon from '@mui/icons-material/Print';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

const ItemListView = ({ displayedItems, startEditing, markAsSold, markAsMissing, deleteItem, checkItemTransactions, archiveItem, channelsMap = {}, onPrintLabel, labelRequiredChannelIds = new Set(), selectedItems = [], onToggleSelect, onSelectAll, sortField = null, sortOrder = 'asc', onSortChange, onRequestBarcodes, isRequestingBarcodes = false, quailhqChannelIds = new Set() }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [quantitySold, setQuantitySold] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);
  const [pendingDeleteAnchorEl, setPendingDeleteAnchorEl] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [itemToDelete, setItemToDelete] = useState(null);
  const [hasTransactions, setHasTransactions] = useState(false);
  const [transactionsCount, setTransactionsCount] = useState(0);
  const [isCheckingTransactions, setIsCheckingTransactions] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [validationError, setValidationError] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleDeleteClick = async (event, item) => {
    const anchorEl = event.currentTarget;
    setPendingDeleteAnchorEl(anchorEl);
    setDeleteConfirmText('');
    setItemToDelete(item);
    setHasTransactions(false);
    setTransactionsCount(0);
    setIsCheckingTransactions(true);
    try {
      if (!checkItemTransactions) {
      }
      const result = checkItemTransactions
        ? await checkItemTransactions(item.id)
        : { hasTransactions: false, count: 0 };
      setHasTransactions(!!result?.hasTransactions);
      setTransactionsCount(result?.count ?? 0);
    } finally {
      setIsCheckingTransactions(false);
      setDeleteAnchorEl(anchorEl);
      setPendingDeleteAnchorEl(null);
    }
  };

  const handleDeleteConfirmClose = () => {
    setDeleteAnchorEl(null);
    setPendingDeleteAnchorEl(null);
    setDeleteConfirmText('');
    setItemToDelete(null);
    setHasTransactions(false);
    setTransactionsCount(0);
    setIsCheckingTransactions(false);
    setIsDeleting(false);
  };

  const handleDeleteConfirmed = async () => {
    if (isDeleting) return;
    setIsDeleting(true);
    if (hasTransactions) {
      if (deleteConfirmText === 'yes') {
        const deleted = await deleteItem(itemToDelete.id);
        if (deleted) {
          handleDeleteConfirmClose();
        } else {
          setIsDeleting(false);
        }
      } else {
        setIsDeleting(false);
      }
    } else {
      const deleted = await deleteItem(itemToDelete.id);
      if (deleted) {
        handleDeleteConfirmClose();
      } else {
        setIsDeleting(false);
      }
    }
  };

  const isDeleteEnabled = hasTransactions ? deleteConfirmText === 'yes' : true;

  const handleMarkAsSoldClick = (event, item) => {
    const itemQty = item.itemQuantity ? parseInt(item.itemQuantity, 10) : 0;
    
    if (itemQty === 1) {
      markAsSold({ ...item, quantitySold: 1 });
    } else if (itemQty > 1) {
      setAnchorEl(event.currentTarget);
      setQuantitySold('');
      setSelectedItem(item);
    } else {
      setValidationError('Cannot mark as sold: No quantity available');
      return;
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setQuantitySold('');
    setSelectedItem(null);
  };

  const handleConfirmSale = () => {
    const qtySold = parseInt(quantitySold, 10);
    const itemQty = parseInt(selectedItem.itemQuantity, 10);
    
    if (isNaN(qtySold) || qtySold <= 0) {
      setValidationError('Please enter a valid quantity');
      return;
    }
    
    if (qtySold > itemQty) {
      setValidationError(`Cannot sell ${qtySold}. Only ${itemQty} available.`);
      return;
    }
    
    markAsSold({ ...selectedItem, quantitySold: qtySold });
    handlePopoverClose();
  };

  const open = Boolean(anchorEl);

  return (
    <>
      {validationError && (
        <Alert severity="warning" onClose={() => setValidationError('')} sx={{ mb: 1 }}>{validationError}</Alert>
      )}
      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table aria-label="Inventory items">
          <TableHead>
            <TableRow>
              {onToggleSelect && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedItems.length > 0 &&
                      selectedItems.length < displayedItems.filter(i => i.itemNumber).length
                    }
                    checked={
                      displayedItems.filter(i => i.itemNumber).length > 0 &&
                      displayedItems.filter(i => i.itemNumber).every(i => selectedItems.includes(i.id))
                    }
                    onChange={onSelectAll}
                    aria-label="Select all items"
                    sx={{
                      color: 'rgba(255,255,255,0.7)',
                      '&.Mui-checked': { color: 'common.white' },
                      '&.MuiCheckbox-indeterminate': { color: 'common.white' },
                    }}
                  />
                </TableCell>
              )}
              {!isMobile && <TableCell sx={{ fontWeight: 700 }}>Image</TableCell>}
              <TableCell sx={{ fontWeight: 700 }}>
                {onSortChange ? (
                  <TableSortLabel
                    active={sortField === 'itemTitle'}
                    direction={sortField === 'itemTitle' ? sortOrder : 'asc'}
                    onClick={() => onSortChange('itemTitle')}
                    sx={{ '&.MuiTableSortLabel-root': { color: 'inherit' }, '&.Mui-active': { color: 'inherit' }, '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                  >
                    Title
                  </TableSortLabel>
                ) : 'Title'}
              </TableCell>
              {!isMobile && (
                <TableCell sx={{ fontWeight: 700 }}>
                  {onSortChange ? (
                    <TableSortLabel
                      active={sortField === 'itemNumber'}
                      direction={sortField === 'itemNumber' ? sortOrder : 'asc'}
                      onClick={() => onSortChange('itemNumber')}
                      sx={{ '&.MuiTableSortLabel-root': { color: 'inherit' }, '&.Mui-active': { color: 'inherit' }, '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                    >
                      Item #
                    </TableSortLabel>
                  ) : 'Item #'}
                </TableCell>
              )}
              {!isMobile && <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>}
              {!isMobile && <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>}
              <TableCell sx={{ fontWeight: 700, textAlign: 'right' }}>
                {onSortChange ? (
                  <TableSortLabel
                    active={sortField === 'listPrice'}
                    direction={sortField === 'listPrice' ? sortOrder : 'asc'}
                    onClick={() => onSortChange('listPrice')}
                    sx={{ '&.MuiTableSortLabel-root': { color: 'inherit' }, '&.Mui-active': { color: 'inherit' }, '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                  >
                    List Price
                  </TableSortLabel>
                ) : 'List Price'}
              </TableCell>
              {!isMobile && <TableCell sx={{ fontWeight: 700, textAlign: 'right' }}>Cost</TableCell>}
              {!isMobile && <TableCell sx={{ fontWeight: 700 }}>Sales Channel</TableCell>}
              {!isMobile && <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>}
              {!isMobile && <TableCell sx={{ fontWeight: 700 }}>Listing</TableCell>}
              {!isMobile && (
                <TableCell sx={{ fontWeight: 700 }}>
                  {onSortChange ? (
                    <TableSortLabel
                      active={sortField === 'itemQuantity'}
                      direction={sortField === 'itemQuantity' ? sortOrder : 'asc'}
                      onClick={() => onSortChange('itemQuantity')}
                      sx={{ '&.MuiTableSortLabel-root': { color: 'inherit' }, '&.Mui-active': { color: 'inherit' }, '& .MuiTableSortLabel-icon': { color: 'inherit !important' } }}
                    >
                      Quantity
                    </TableSortLabel>
                  ) : 'Quantity'}
                </TableCell>
              )}
              <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedItems.map((item, index) => {
              const isSold = item.isSold;
              const isMissing = item.isMissing ?? false;
              const statusColor = isMissing ? 'error' : isSold ? 'success' : 'primary';
              const statusLabel = isMissing ? 'Missing' : isSold ? 'Sold' : 'Available';
              const rowKey = item.id || item.itemTitle || `${item.itemTitle || 'item'}-${index}`;

              return (
                <TableRow
                  key={rowKey}
                  sx={{
                    opacity: isSold ? 0.7 : 1,
                    '&:hover': { backgroundColor: 'grey.50' },
                  }}
                >
                  {onToggleSelect && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedItems.includes(item.id)}
                        onChange={() => onToggleSelect(item.id)}
                        disabled={!item.itemNumber}
                        aria-label={`Select ${item.itemTitle}`}
                      />
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>
                      {item.mainImageUrl ? (
                        <Avatar
                          src={item.mainImageUrl}
                          alt={item.itemTitle}
                          variant="rounded"
                          sx={{ width: 60, height: 60 }}
                        />
                      ) : (
                        <Avatar
                          variant="rounded"
                          sx={{ width: 60, height: 60, backgroundColor: '#f5f5f5' }}
                          aria-hidden="true"
                        >
                          <ImageIcon sx={{ color: '#bdbdbd' }} />
                        </Avatar>
                      )}
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.itemTitle}
                    </Typography>
                  </TableCell>
                  {!isMobile && (
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {item.itemNumber || ''}
                      </Typography>
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 200, wordBreak: 'break-word' }}>
                        {item.description}
                      </Typography>
                    </TableCell>
                  )}
                  {!isMobile && (
                  <TableCell>
                    <Chip
                      icon={isSold ? <CheckCircleIcon /> : undefined}
                      label={statusLabel}
                      color={statusColor}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  )}
                  <TableCell sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.dark' }}>
                      ${parseFloat(item.listPrice).toFixed(2)}
                    </Typography>
                  </TableCell>
                  {!isMobile && (
                    <TableCell sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.dark' }}>
                        ${parseFloat(item.cost).toFixed(2)}
                      </Typography>
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {quailhqChannelIds.has(item.salesChannelId) && (
                          <Box component="img" src="/assets/quail-hq-logo.png" alt="QuailHQ" sx={{ height: 16, width: 'auto' }} />
                        )}
                        <Typography variant="body2">
                          {channelsMap[item.salesChannelId] || 'Unassigned'}
                        </Typography>
                      </Box>
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>
                      <Typography variant="body2">
                        {item.salesLocation || ''}
                      </Typography>
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>
                      {item.itemLink && (item.itemLink.startsWith('http://') || item.itemLink.startsWith('https://')) ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography
                            variant="body2"
                            component="a"
                            href={item.itemLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              color: 'primary.main',
                              textDecoration: 'none',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: 150,
                              display: 'block',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {item.itemLink}
                          </Typography>
                          <OpenInNewIcon sx={{ fontSize: 14, color: 'primary.main', flexShrink: 0 }} />
                        </Box>
                      ) : (
                        <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                          {item.itemLink || ''}
                        </Typography>
                      )}
                    </TableCell>
                  )}
                  {!isMobile && (
                    <TableCell>
                      <Typography variant="body2">
                        {item.itemQuantity ?? 0}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      {item.itemNumber && onPrintLabel && labelRequiredChannelIds.has(item.salesChannelId) && (
                        <Tooltip title="Print Label">
                          <IconButton size="small" onClick={() => onPrintLabel(item)} aria-label="Print Label">
                            <PrintIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onRequestBarcodes && quailhqChannelIds.has(item.salesChannelId) && (
                        <Tooltip title="Request QuailHQ Barcode">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => onRequestBarcodes([item.id])}
                              disabled={isRequestingBarcodes}
                              aria-label="Request QuailHQ Barcode"
                            >
                              {isRequestingBarcodes ? <CircularProgress size={16} /> : <QrCode2Icon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => startEditing(item)} aria-label="Edit">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!isSold && (
                        parseInt(item.itemQuantity, 10) > 0 ? (
                          <Tooltip title="Sell">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={(e) => handleMarkAsSoldClick(e, item)}
                              disabled={isMissing}
                              aria-label="Sell"
                            >
                              <AttachMoneyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Sold">
                            <span>
                              <IconButton size="small" color="success" disabled aria-label="Sold">
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )
                      )}
                      {isSold && parseInt(item.itemQuantity, 10) === 0 && archiveItem && (
                        <Tooltip title="Archive">
                          <IconButton size="small" color="secondary" onClick={() => archiveItem(item)} aria-label="Archive">
                            <ArchiveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!isSold && markAsMissing && (
                        <Tooltip title={isMissing ? 'Mark as Found' : 'Mark as Missing'}>
                          <IconButton
                            size="small"
                            color={isMissing ? 'warning' : 'default'}
                            onClick={() => markAsMissing(item)}
                            aria-label={isMissing ? 'Mark as Found' : 'Mark as Missing'}
                          >
                            <SearchOffIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => handleDeleteClick(e, item)}
                            disabled={isCheckingTransactions || !!pendingDeleteAnchorEl}
                            aria-label="Delete"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Quantity Sold Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, minWidth: 250 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Enter Quantity Sold
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
            Available: {selectedItem?.itemQuantity}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="number"
            label="Quantity Sold"
            value={quantitySold}
            onChange={(e) => setQuantitySold(e.target.value)}
            inputProps={{ min: 1, max: selectedItem?.itemQuantity }}
            size="small"
            sx={{ mb: 2 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleConfirmSale();
              }
            }}
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" onClick={handlePopoverClose}>
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={handleConfirmSale}
            >
              Confirm
            </Button>
          </Stack>
        </Box>
      </Popover>

      {/* Delete Confirmation Popover */}
      <Popover
        open={Boolean(deleteAnchorEl)}
        anchorEl={deleteAnchorEl}
        onClose={handleDeleteConfirmClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 3, minWidth: 350, maxWidth: 400 }}>
          <Typography variant="body2" sx={{ mb: 3 }}>
            {isCheckingTransactions
              ? 'Checking for related transactions...'
              : hasTransactions
                ? `This item has ${transactionsCount} related sales transaction${transactionsCount === 1 ? '' : 's'}. Deleting the item will delete these transactions and affect your sales reporting. Are you sure you want to delete this item and the related transaction(s)?`
                : 'Deleting an item is irreversible. The item and all associated data & images will be deleted. Are you sure?'}
          </Typography>
          {hasTransactions && (
            <TextField
              fullWidth
              label="Type 'yes' to delete the item and related transaction(s)"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              size="small"
              sx={{ mb: 2 }}
              placeholder="yes"
            />
          )}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button size="small" onClick={handleDeleteConfirmClose}>
              {itemToDelete?.isSold ? 'No, go back' : 'Go Back'}
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              onClick={handleDeleteConfirmed}
              disabled={isCheckingTransactions || isDeleting || !isDeleteEnabled}
            >
              {isDeleting ? <CircularProgress size={18} color="inherit" /> : 'Yes, Delete'}
            </Button>
          </Stack>
        </Box>
      </Popover>
    </>
  );
};

export default ItemListView;
